import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, Typography, Link as MUILink, CircularProgress, Alert, Breadcrumbs, IconButton, Tooltip, Collapse, Divider } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Minimal in-app Markdown viewer that auto-renders Mermaid via global MermaidRenderer
// Usage: /docs/view?file=/docs/architecture/tech-stack-architecture.md

const getParam = (name: string) => new URLSearchParams(window.location.search).get(name);

class LocalErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; msg?: string }>{
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error: unknown) {
    const msg = error instanceof Error ? error.message : (typeof error === 'object' && error !== null ? JSON.stringify(error) : String(error));
    return { hasError: true, msg };
  }
  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Docs markdown render error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return <Alert severity="error">Markdown render failed: {this.state.msg}</Alert>;
    }
    return <>{this.props.children}</>;
  }
}

const DocsViewer: React.FC = () => {
  const fileParam = useMemo(() => getParam('file') || '/docs/architecture/tech-stack-architecture.md', []);
  const noMermaid = useMemo(() => getParam('noMermaid') === '1', []);
  const [content, setContent] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [version, setVersion] = useState<number | null>(null);
  const [docId, setDocId] = useState<string | null>(null);
  const [showRevisions, setShowRevisions] = useState<boolean>(false);
  const [revisions, setRevisions] = useState<Array<{ _id: string; version: number; createdAt?: string; updatedAt?: string }>>([]);
  const [importing, setImporting] = useState<boolean>(false);
  const [statusMsg, setStatusMsg] = useState<string>('');
  const apiBase = useMemo(() => {
    let env = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    if (env.endsWith('/api/v1')) env = env.replace(/\/api\/v1$/, '');
    if (env) return env;
    // Fallback heuristics for local dev: assume API on :3002 when UI on :3008
    try {
      const origin = window.location.origin;
      if (origin.includes(':3008')) return origin.replace(':3008', ':3002');
      // Production fallback: use Railway API
      if (origin.includes('pages.dev') || origin.includes('e-unify.ai')) {
        return 'https://e-unify-app-production.up.railway.app';
      }
      return origin;
    } catch {
      return '';
    }
  }, []);

  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error('DocsViewer load error:', error);
    }
  }, [error]);

  useEffect(() => {
    // Global error handler to catch any unhandled errors in this component
    const handleGlobalError = (event: ErrorEvent) => {
      event.preventDefault();
      const errObj = event.error;
      let msg = 'Unknown error';
      if (errObj instanceof Error) {
        msg = errObj.message;
      } else if (typeof errObj === 'object' && errObj !== null) {
        msg = JSON.stringify(errObj);
      } else if (event.message) {
        msg = event.message;
      } else {
        msg = String(errObj || event);
      }
      // eslint-disable-next-line no-console
      console.error('Global error caught in DocsViewer:', msg, errObj, event);
      setError(`Rendering error: ${msg}`);
      return true;
    };
    
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      event.preventDefault();
      const reason = event.reason;
      let msg = 'Unhandled promise rejection';
      if (reason instanceof Error) {
        msg = reason.message;
      } else if (typeof reason === 'object' && reason !== null) {
        msg = JSON.stringify(reason);
      } else {
        msg = String(reason);
      }
      // eslint-disable-next-line no-console
      console.error('Unhandled rejection in DocsViewer:', msg, reason);
      setError(`Promise rejection: ${msg}`);
    };
    
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tryFetch = async (url: string): Promise<string> => {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const ct = res.headers.get('content-type') || '';
      const text = await res.text();
      if (ct.includes('text/html') && !text.startsWith('#') && !text.startsWith('```')) {
        throw new Error(`Unexpected HTML response for ${url}. Ensure the path points to a .md under /docs.`);
      }
      return text;
    };

    const run = async () => {
      setLoading(true);
      setError('');
      try {
        // Try Atlas-backed docs API first
        if (apiBase) {
          try {
            const apiUrl = `${apiBase}/api/v1/docs?path=${encodeURIComponent(fileParam)}`;
            const r = await fetch(apiUrl, { credentials: 'include' });
            if (r.ok) {
              const j = await r.json();
              if (j && j.success && j.data && typeof j.data.content === 'string') {
                const loadedApi = j.data.content as string;
                const ver = typeof j.data.version === 'number' ? j.data.version : null;
                const id = typeof j.data._id === 'string' ? j.data._id : null;
                let processed = loadedApi;
                try {
                  const today = new Date().toISOString().slice(0, 10);
                  processed = loadedApi.replace(
                    /Last updated:\s*<!--\s*set on edit\s*-->/i,
                    `Last updated: ${today}`
                  );
                } catch (_) {}
                if (!cancelled) {
                  setContent(processed);
                  setVersion(ver);
                  setDocId(id);
                  return;
                }
              }
            }
          } catch (_) {}
        }

        // Build candidate URLs for static fallback
        const candidates: string[] = [];
        if (fileParam.startsWith('/docs') && apiBase) {
          candidates.push(`${apiBase}${fileParam}`);
        }
        candidates.push(fileParam);
        if (!fileParam.startsWith('/docs')) {
          candidates.push(`/docs${fileParam.startsWith('/') ? '' : '/'}${fileParam}`);
        }
        const pub = fileParam.replace(/^\/docs\//, '/public/docs/');
        candidates.push(pub);

        let loaded = '';
        let lastErr: any;
        for (const url of candidates) {
          try {
            loaded = await tryFetch(url);
            if (loaded) break;
          } catch (e) {
            lastErr = e;
          }
        }
        if (!loaded) throw lastErr || new Error('No content fetched');
        // Replace 'Last updated: <!-- set on edit -->' with today's date at render time only
        let processed = loaded;
        try {
          const today = new Date().toISOString().slice(0, 10);
          processed = loaded.replace(
            /Last updated:\s*<!--\s*set on edit\s*-->/i,
            `Last updated: ${today}`
          );
        } catch (_) {
          // noop; fallback to original content if any unexpected error during substitution
        }
        if (!cancelled) {
          setContent(processed);
          setVersion(null);
          setDocId(null);
        }
      } catch (ePrimary: any) {
        // Fallbacks: try /docs prefix or public copy
        const errMsg = ePrimary instanceof Error ? ePrimary.message : (typeof ePrimary === 'object' && ePrimary !== null ? JSON.stringify(ePrimary) : String(ePrimary));
        if (!cancelled) setError(`Failed to load ${fileParam}: ${errMsg}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [fileParam]);

  useEffect(() => {
    // After markdown is rendered, trigger Mermaid render if available
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mr: any = (window as any).MermaidRenderer;
    if (!error && !noMermaid && mr && typeof mr.render === 'function') {
      // Defer to next tick so DOM updates are present and guard errors
      setTimeout(() => {
        try {
          mr.render(document);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Mermaid render failed:', e);
          const errMsg = e instanceof Error ? e.message : (typeof e === 'object' && e !== null ? JSON.stringify(e) : String(e));
          setError(`Mermaid rendering failed: ${errMsg}`);
        }
      }, 0);
    }
  }, [content, error, noMermaid]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MUILink href="/" underline="hover" color="inherit">Home</MUILink>
        <MUILink href="/docs" underline="hover" color="inherit">Docs</MUILink>
        <Typography color="text.primary">Viewer</Typography>
      </Breadcrumbs>

      <Typography variant="h4" component="h1" sx={{ color: '#003366', mb: 2 }}>
        Documentation Viewer
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">Mermaid-enabled Markdown renderer</Typography>
        <Tooltip title="About C4 System Context (C1)">
          <IconButton aria-label="About C4 System Context" onClick={() => setShowInfo((v) => !v)} size="small">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={showInfo} unmountOnExit>
        <Box role="region" aria-label="C4 System Context information" sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2, bgcolor: '#fafafa' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>System Context (C4‑C1)</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The System Context diagram (C4‑C1) shows your software system at the center, surrounded by its users and the external systems it interacts with. It provides a high‑level overview for both technical and non‑technical stakeholders.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Key points: the system in scope, people/roles, and external systems; relationships are shown as lines. This view focuses on the big picture and is ideal for onboarding, compliance, and framing deeper container/component views.
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" sx={{ mb: 1 }}>
            References:
          </Typography>
          <ul style={{ marginTop: 0 }}>
            <li><MUILink href="https://c4model.com/diagrams/system-context" target="_blank" rel="noreferrer">C4 — System Context</MUILink></li>
            <li><MUILink href="https://c4model.info" target="_blank" rel="noreferrer">c4model.info</MUILink></li>
            <li><MUILink href="https://miro.com/diagramming/c4-model-for-software-architecture/" target="_blank" rel="noreferrer">Miro — C4 Model</MUILink></li>
            <li><MUILink href="https://icepanel.io/blog/2024-08-20-system-landscape-diagrams-c4-model" target="_blank" rel="noreferrer">Icepanel — System Landscape & C4</MUILink></li>
            <li><MUILink href="https://www.port.io/glossary/c4-model" target="_blank" rel="noreferrer">Port — C4 Glossary</MUILink></li>
            <li><MUILink href="https://www.valueblue.com/blog/applying-the-c4-model-within-an-enterprise-architecture-tool" target="_blank" rel="noreferrer">ValueBlue — Applying C4</MUILink></li>
            <li><MUILink href="https://docs.2gis.com/en/on-premise/architecture/c4-diagrams" target="_blank" rel="noreferrer">2GIS — C4 Diagrams</MUILink></li>
            <li><MUILink href="https://en.wikipedia.org/wiki/C4_model" target="_blank" rel="noreferrer">Wikipedia — C4 model</MUILink></li>
            <li><MUILink href="https://www.lucidchart.com/blog/c4-model" target="_blank" rel="noreferrer">Lucidchart — C4 Model</MUILink></li>
            <li><MUILink href="https://www.reddit.com/r/softwarearchitecture/comments/1ezny8k/how_to_create_software_architecture_diagrams/" target="_blank" rel="noreferrer">Reddit — Creating architecture diagrams</MUILink></li>
            <li><MUILink href="https://devot.team/blog/c4-model" target="_blank" rel="noreferrer">Devot — C4 Model</MUILink></li>
          </ul>
        </Box>
      </Collapse>

      <Typography variant="body2" sx={{ mb: 2 }}>
        Viewing: <code>{fileParam}</code> {noMermaid && <Typography component="span" variant="caption" color="warning.main">(Mermaid disabled)</Typography>}
      </Typography>

      {/* Toolbar: Import or Revisions */}
      {!error && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {version === null ? (
            <button
              onClick={async () => {
                setImporting(true);
                setStatusMsg('Importing…');
                try {
                  const url = `${apiBase}/api/v1/docs`;
                  const body = { path: fileParam, content } as any;
                  const r = await fetch(url, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
                  if (!r.ok) throw new Error(await r.text());
                  const j = await r.json();
                  const id = j?.data?._id || null;
                  const ver = typeof j?.data?.version === 'number' ? j.data.version : 1;
                  setDocId(id);
                  setVersion(ver);
                  setStatusMsg('Imported to Atlas');
                } catch (ie) {
                  const msg = ie instanceof Error ? ie.message : String(ie);
                  setStatusMsg(`Error: ${msg}`);
                } finally {
                  setImporting(false);
                }
              }}
              disabled={importing || !apiBase}
              aria-label="Import document to Atlas"
            >Import to Atlas</button>
          ) : (
            <>
              <button
                onClick={async () => {
                  if (!docId) return;
                  setShowRevisions((v) => !v);
                  if (!showRevisions) {
                    // lazy load revisions
                    try {
                      const r = await fetch(`${apiBase}/api/v1/docs/${docId}/revisions?limit=20`, { credentials: 'include' });
                      if (!r.ok) throw new Error(await r.text());
                      const j = await r.json();
                      setRevisions(Array.isArray(j?.data) ? j.data : []);
                    } catch (re) {
                      const msg = re instanceof Error ? re.message : String(re);
                      setStatusMsg(`Error loading revisions: ${msg}`);
                    }
                  }
                }}
                aria-expanded={showRevisions}
                aria-controls="revisions-panel"
              >{showRevisions ? 'Hide Revisions' : 'View Revisions'}</button>
              {version !== null && (
                <Typography variant="caption" sx={{ ml: 1 }}>Current v{version}</Typography>
              )}
            </>
          )}
        </Box>
      )}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} /> <Typography>Loading…</Typography>
        </Box>
      )}
      {!!error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {String(error)}
        </Alert>
      )}

      {/* Revisions panel */}
      {showRevisions && !error && (
        <Box id="revisions-panel" role="region" aria-label="Revisions" sx={{ mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
          {revisions.length === 0 ? (
            <Typography variant="body2">No revisions found.</Typography>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {revisions.map((rev) => (
                <li key={rev._id} style={{ marginBottom: 6 }}>
                  <Typography variant="body2" component="span">v{rev.version}</Typography>
                  <Typography variant="caption" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                    {rev.updatedAt || rev.createdAt || ''}
                  </Typography>
                  <button
                    style={{ marginLeft: 8 }}
                    onClick={async () => {
                      if (!docId) return;
                      if (!window.confirm(`Rollback to version v${rev.version}? This creates a new head.`)) return;
                      try {
                        const r = await fetch(`${apiBase}/api/v1/docs/${docId}/rollback`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ version: rev.version }) });
                        if (!r.ok) throw new Error(await r.text());
                        const j = await r.json();
                        const saved = j?.data?.content || content;
                        const ver = typeof j?.data?.version === 'number' ? j.data.version : version;
                        setContent(saved);
                        setVersion(ver || null);
                        setStatusMsg(`Rolled back to v${rev.version}`);
                        setShowRevisions(false);
                      } catch (re) {
                        const msg = re instanceof Error ? re.message : String(re);
                        setStatusMsg(`Error: ${msg}`);
                      }
                    }}
                    aria-label={`Rollback to version ${rev.version}`}
                  >Rollback</button>
                </li>
              ))}
            </ul>
          )}
        </Box>
      )}

      {!error && !editing && (
        <LocalErrorBoundary>
          <Box
            id="markdown-root"
            tabIndex={0}
            role="button"
            aria-label="Edit document"
            onClick={() => { setEditing(true); setDraft(content); setStatusMsg(''); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(true); setDraft(content); setStatusMsg(''); }
            }}
            sx={{ '& pre': { backgroundColor: '#f5f5f5', p: 1, borderRadius: 1 }, outline: 'none', '&:focus': { boxShadow: '0 0 0 3px #00336655' }, cursor: 'pointer' }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || '# No content loaded'}</ReactMarkdown>
          </Box>
        </LocalErrorBoundary>
      )}

      {!error && editing && (
        <Box role="region" aria-label="Editor" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Editing (Press Esc to cancel)</Typography>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Document editor"
            style={{ width: '100%', minHeight: 320, fontFamily: 'monospace', fontSize: 14, lineHeight: 1.5, padding: 12 }}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                (async () => {
                  setSaving(true);
                  setStatusMsg('Saving…');
                  try {
                    const url = `${apiBase}/api/v1/docs`;
                    const body = { path: fileParam, content: draft } as any;
                    if (version !== null) body.version = version;
                    const r = await fetch(url, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
                    if (r.status === 409) {
                      const j = await r.json();
                      setStatusMsg(`Version conflict. Current version is ${j?.data?.currentVersion}. Reload to merge.`);
                    } else if (!r.ok) {
                      const t = await r.text();
                      throw new Error(t || 'Save failed');
                    } else {
                      const j = await r.json();
                      const saved = j?.data?.content || draft;
                      const ver = typeof j?.data?.version === 'number' ? j.data.version : version;
                      setContent(saved);
                      setVersion(ver || null);
                      setEditing(false);
                      setStatusMsg('Saved');
                    }
                  } catch (se) {
                    const msg = se instanceof Error ? se.message : String(se);
                    setStatusMsg(`Error: ${msg}`);
                  } finally {
                    setSaving(false);
                  }
                })();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setEditing(false);
                setStatusMsg('');
              }
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <button
              onClick={async () => {
                setSaving(true);
                setStatusMsg('Saving…');
                try {
                  const url = `${apiBase}/api/v1/docs`;
                  const body = { path: fileParam, content: draft } as any;
                  if (version !== null) body.version = version;
                  const r = await fetch(url, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
                  if (r.status === 409) {
                    const j = await r.json();
                    setStatusMsg(`Version conflict. Current version is ${j?.data?.currentVersion}. Reload to merge.`);
                  } else if (!r.ok) {
                    const t = await r.text();
                    throw new Error(t || 'Save failed');
                  } else {
                    const j = await r.json();
                    const saved = j?.data?.content || draft;
                    const ver = typeof j?.data?.version === 'number' ? j.data.version : version;
                    setContent(saved);
                    setVersion(ver || null);
                    setEditing(false);
                    setStatusMsg('Saved');
                  }
                } catch (se) {
                  const msg = se instanceof Error ? se.message : String(se);
                  setStatusMsg(`Error: ${msg}`);
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
              aria-label="Save document"
            >Save</button>
            <button onClick={() => { setEditing(false); setStatusMsg(''); }} aria-label="Cancel editing">Cancel</button>
            {version !== null && (
              <Typography variant="caption" sx={{ ml: 1 }}>v{version}</Typography>
            )}
          </Box>
          <Box aria-live="polite" role="status" style={{ marginTop: 8 }}>
            <Typography variant="caption" color={statusMsg.startsWith('Error') ? 'error' : 'text.secondary'}>{statusMsg}</Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 4 }}>
        <Typography variant="body2">
          Tips: Use the Render Test in your docs to verify Mermaid. For architecture: <MUILink href="/docs/view?file=/docs/architecture/tech-stack-architecture.md">Tech Stack Architecture</MUILink>.
          Demo page: <MUILink href="/docs/mermaid-demo.html">Mermaid Demo</MUILink>
        </Typography>
      </Box>
    </Container>
  );
};

export default DocsViewer;
